import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import useAudioStore from '../store/audioStore';
import { useLastRead } from '../hooks/useLastRead';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useBookmarks } from '../hooks/useBookmarks';
import { useNotes } from '../hooks/useNotes';
import BookmarksNotesPanel from './BookmarksNotesPanel';
import NoteModal from './NoteModal';
import SearchModal from './SearchModal';
import IndexModal from './IndexModal';
import BottomNavigation from './BottomNavigation';
import SettingsDrawer from './SettingsDrawer';

const SurahDetail = () => {
  const { number } = useParams();
  const [surah, setSurah] = useState(null);
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState(null);
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('fontSize');
    const parsed = saved ? parseInt(saved, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : 32;
  });
  const [lineSpacing, setLineSpacing] = useState(() => {
    return localStorage.getItem('lineSpacing') || 'normal';
  });
  const [fontType, setFontType] = useState(() => {
    return localStorage.getItem('fontType') || 'uthmani';
  });
  const [showEnglish, setShowEnglish] = useState(true);
  const [showUrdu, setShowUrdu] = useState(true);
  const [copiedAyah, setCopiedAyah] = useState(null);
  const [readingMode, setReadingMode] = useState(() => {
    return localStorage.getItem('readingMode') || 'scroll';
  });
  const [currentAyahIndex, setCurrentAyahIndex] = useState(0);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showIndexModal, setShowIndexModal] = useState(false);
  const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);
  const [allSurahs, setAllSurahs] = useState([]);
  
  const ayahRefs = useRef({});
  const ayahContainerRef = useRef(null);
  const justStoppedRef = useRef(false);
  const [targetScrollAyah, setTargetScrollAyah] = useState(null);
  
  // Global audio store
  const { 
    playFullSurah, 
    playAyah, 
    togglePlayPause,
    stop,
    currentSurah, 
    currentAyah, 
    status,
    mode 
  } = useAudioStore();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { lastRead, saveLastRead } = useLastRead();
  const hasAppliedUrlParamsRef = useRef(false);
  const [savedFeedback, setSavedFeedback] = useState(false);
  const [showBookmarksPanel, setShowBookmarksPanel] = useState(false);
  const [activeNoteAyah, setActiveNoteAyah] = useState(null);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const { toggleBookmark, isBookmarked, hasBookmarks } = useBookmarks();
  const { hasNote } = useNotes();

  // Set document title
  useEffect(() => {
    if (surah) {
      document.title = `${surah.englishName} - Quran`;
    }
    return () => {
      document.title = 'Quran';
    };
  }, [surah]);

  // Persist reading mode
  useEffect(() => {
    localStorage.setItem('readingMode', readingMode);
  }, [readingMode]);

  // Persist font size
  useEffect(() => {
    localStorage.setItem('fontSize', String(fontSize));
  }, [fontSize]);

  // Persist line spacing
  useEffect(() => {
    localStorage.setItem('lineSpacing', lineSpacing);
  }, [lineSpacing]);

  // Persist font type
  useEffect(() => {
    localStorage.setItem('fontType', fontType);
  }, [fontType]);

  const getFontFamily = (ft) => {
    switch (ft) {
      case 'indopak': return "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif";
      case 'tajweed': return "'Scheherazade New', 'Amiri', serif";
      default: return "'Amiri', 'Traditional Arabic', serif";
    }
  };

  const arabicFontFamily = getFontFamily(fontType);

  // Reset ayah index when changing surahs
  useEffect(() => {
    setCurrentAyahIndex(0);
    hasAppliedUrlParamsRef.current = false;
    setTargetScrollAyah(null);
  }, [number]);

  // Reset flag when searchParams changes so same-surah bookmark links work
  useEffect(() => {
    hasAppliedUrlParamsRef.current = false;
  }, [searchParams]);

  // Fetch all surahs for search/index
  useEffect(() => {
    const fetchAllSurahs = async () => {
      try {
        const response = await fetch('/api/surahs');
        if (response.ok) {
          const data = await response.json();
          setAllSurahs(data.data || []);
        }
      } catch (err) {
        console.error('Error fetching all surahs:', err);
      }
    };

    fetchAllSurahs();
  }, []);

  // Fetch surah data
  useEffect(() => {
    const fetchSurahDetail = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/surahs/${number}`);

        if (!response.ok) {
          throw new Error('Failed to fetch surah details');
        }

        const data = await response.json();
        setSurah(data.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching surah details:', err);
        setError(err.message || 'Failed to load surah');
      } finally {
        setLoading(false);
      }
    };

    fetchSurahDetail();
  }, [number]);

  // Load ?ayah= and ?mode= from URL, scroll/focus to that ayah
  useEffect(() => {
    if (!surah || hasAppliedUrlParamsRef.current) return;
    const ayahParam = searchParams.get('ayah');
    const modeParam = searchParams.get('mode');
    const ayahNum = ayahParam ? parseInt(ayahParam, 10) : null;
    const targetMode = modeParam === 'ayah' ? 'ayah' : 'scroll';
    if (ayahNum >= 1 && ayahNum <= surah.ayahs.length) {
      hasAppliedUrlParamsRef.current = true;
      setReadingMode(targetMode);
      setCurrentAyahIndex(ayahNum - 1);
      if (targetMode === 'scroll') {
        setTargetScrollAyah(ayahNum);
      }
    }
  }, [surah, searchParams]);

  // Scroll to targetScrollAyah after the DOM has rendered the scroll-mode cards
  useEffect(() => {
    if (!targetScrollAyah || readingMode !== 'scroll') return;
    const el = ayahRefs.current[targetScrollAyah];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTargetScrollAyah(null);
    }
  }, [targetScrollAyah, readingMode, surah]);

  // Find topmost visible ayah (for manual save in scroll mode)
  const getVisibleAyahNumber = useCallback(() => {
    const viewportTop = window.scrollY + 150;
    let bestAyah = 1;
    let bestDist = Infinity;
    Object.entries(ayahRefs.current).forEach(([key, el]) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const top = rect.top + window.scrollY;
      const dist = Math.abs(top - viewportTop);
      if (dist < bestDist) {
        bestDist = dist;
        bestAyah = parseInt(key, 10);
      }
    });
    return bestAyah >= 1 ? bestAyah : 1;
  }, []);

  const handleSavePlace = useCallback(() => {
    if (!surah) return;
    const ayahNum = readingMode === 'ayah' ? currentAyahIndex + 1 : getVisibleAyahNumber();
    saveLastRead(parseInt(number), ayahNum, readingMode, {
      translationLang: showEnglish && showUrdu ? 'en-urdu' : showEnglish ? 'en' : 'urdu',
    });
    setSavedFeedback(true);
    setTimeout(() => setSavedFeedback(false), 2000);
  }, [surah, number, readingMode, currentAyahIndex, getVisibleAyahNumber, saveLastRead, showEnglish, showUrdu]);

  const handleResumeToLastRead = useCallback(() => {
    if (!lastRead) return;
    const { surahNumber, ayahNumber, readingMode: mode } = lastRead;
    if (parseInt(number) === surahNumber) {
      setReadingMode(mode);
      setCurrentAyahIndex(ayahNumber - 1);
      if (mode === 'scroll') {
        setTimeout(() => {
          const el = ayahRefs.current[ayahNumber];
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    } else {
      navigate(`/surah/${surahNumber}?ayah=${ayahNumber}&mode=${mode}`);
    }
  }, [lastRead, number, navigate]);

  const handleToggleAyahBookmark = useCallback((ayah) => {
    toggleBookmark({
      type: 'ayah',
      surahNumber: parseInt(number),
      surahName: surah.name,
      surahEnglishName: surah.englishName,
      ayahNumber: ayah.numberInSurah,
      ayahText: ayah.english,
    });
  }, [toggleBookmark, number, surah]);

  const handleToggleSurahBookmark = useCallback(() => {
    if (!surah) return;
    toggleBookmark({
      type: 'surah',
      surahNumber: parseInt(number),
      surahName: surah.name,
      surahEnglishName: surah.englishName,
    });
  }, [toggleBookmark, number, surah]);

  const handleOpenNote = useCallback((ayah) => {
    setActiveNoteAyah(ayah);
    setIsNoteModalOpen(true);
  }, []);

  const handleToggleFocusMode = useCallback(() => {
    setReadingMode(prev => prev === 'ayah' ? 'scroll' : 'ayah');
  }, []);

  useKeyboardShortcuts({
    onFocusMode: handleToggleFocusMode,
  });

  // Auto-scroll to active ayah (only in scroll mode)
  useEffect(() => {
    if (readingMode === 'scroll' && currentSurah === parseInt(number) && currentAyah && ayahRefs.current[currentAyah]) {
      const element = ayahRefs.current[currentAyah];
      const offset = -200; // Keep ayah comfortably positioned
      
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset + offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, [currentAyah, currentSurah, number, readingMode]);

  // Sync Ayah Focus mode with audio player state
  useEffect(() => {
    if (
      readingMode === 'ayah' && 
      currentSurah === parseInt(number) && 
      currentAyah && 
      surah &&
      status === 'playing' &&
      mode === 'single-ayah'
    ) {
      // Convert currentAyah (numberInSurah) to zero-based index
      const ayahIndex = currentAyah - 1;
      if (ayahIndex >= 0 && ayahIndex < surah.ayahs.length) {
        setCurrentAyahIndex(ayahIndex);
      }
    }
  }, [currentAyah, currentSurah, number, readingMode, surah, status, mode]);

  // Navigation handlers (defined before swipe effect)
  const handlePrevAyah = useCallback(() => {
    if (currentAyahIndex > 0) {
      setCurrentAyahIndex(prev => prev - 1);
    }
  }, [currentAyahIndex]);

  const handleNextAyah = useCallback(() => {
    if (surah && currentAyahIndex < surah.ayahs.length - 1) {
      setCurrentAyahIndex(prev => prev + 1);
    }
  }, [surah, currentAyahIndex]);

  // Swipe gesture support for ayah mode
  useEffect(() => {
    if (readingMode !== 'ayah' || !ayahContainerRef.current || !surah) return;

    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].screenX;
      handleSwipe();
    };

    const handleSwipe = () => {
      const swipeThreshold = 50;
      const diff = touchStartX - touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          // Swipe left - next ayah
          handleNextAyah();
        } else {
          // Swipe right - previous ayah
          handlePrevAyah();
        }
      }
    };

    const container = ayahContainerRef.current;
    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [readingMode, surah, currentAyahIndex, handleNextAyah, handlePrevAyah]);

  const handlePlayFullSurah = () => {
    if (isFullSurahPlaying()) {
      justStoppedRef.current = true;
      stop();
      setTimeout(() => { justStoppedRef.current = false; }, 400);
      return;
    }
    if (justStoppedRef.current) return;
    playFullSurah(parseInt(number));
  };

  const handlePlayAyah = (ayahNumber, absoluteAyahNumber) => {
    const surahNumber = parseInt(number);
    const isSameAyah = currentSurah === surahNumber && currentAyah === ayahNumber;
    const isSingleAyahMode = mode === 'single-ayah';

    if (isSameAyah && isSingleAyahMode && (status === 'playing' || status === 'paused')) {
      togglePlayPause();
      return;
    }

    playAyah(surahNumber, ayahNumber, absoluteAyahNumber);
  };

  const copyAyah = (arabic, english, urdu, ayahNumber) => {
    let text = arabic;
    if (showUrdu) text += `\n\n${urdu}`;
    if (showEnglish) text += `\n\n${english}`;
    
    navigator.clipboard.writeText(text).then(() => {
      setCopiedAyah(ayahNumber);
      setTimeout(() => setCopiedAyah(null), 2000);
    });
  };

  const getLineHeightClass = () => {
    switch (lineSpacing) {
      case 'compact': return 1.6;
      case 'relaxed': return 2.4;
      default: return 2.0;
    }
  };

  const isCurrentAyahPlaying = (ayahNumber) => {
    return currentSurah === parseInt(number) && currentAyah === ayahNumber && status === 'playing';
  };

  const isFullSurahPlaying = () => {
    return currentSurah === parseInt(number) && mode === 'full-surah' && status === 'playing';
  };

  // Loading State
  if (loading) {
    return (
      <div 
        style={{ 
          background: 'var(--bg-primary)',
          minHeight: 'var(--app-height)',
          paddingTop: 'var(--safe-area-top)',
          paddingBottom: 'var(--safe-area-bottom)',
        }}
      >
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg mb-8 transition-colors"
            style={{
              color: 'var(--text-urdu)',
              background: 'rgba(148, 163, 184, 0.08)',
            }}
          >
            <span>←</span>
            <span>Back</span>
            </Link>
          
          <div className="text-center py-20">
            <div className="inline-block animate-shimmer rounded-xl p-8 mb-4">
              <div className="text-5xl">📖</div>
          </div>
            <p className="font-medium" style={{ color: 'var(--text-secondary)' }}>
              Loading surah...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !surah) {
    return (
      <div 
        style={{ 
          background: 'var(--bg-primary)',
          minHeight: 'var(--app-height)',
          paddingTop: 'var(--safe-area-top)',
          paddingBottom: 'var(--safe-area-bottom)',
        }}
      >
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg mb-8"
            style={{
              color: 'var(--text-urdu)',
              background: 'rgba(148, 163, 184, 0.08)',
            }}
          >
            <span>←</span>
            <span>Back</span>
          </Link>
          
          <div className="text-center py-20">
            <div className="text-5xl mb-4">⚠️</div>
            <p className="text-lg font-semibold mb-4" style={{ color: 'var(--error)' }}>
              {error || 'Surah not found'}
            </p>
            <Link 
              to="/"
              className="inline-block px-6 py-3 rounded-lg font-medium transition-colors"
              style={{
                background: 'var(--gold-muted)',
                color: 'var(--gold-hover)',
                border: '1px solid var(--border-gold)',
              }}
            >
              Return to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Main Content
  return (
    <div
      style={{
        background: 'var(--bg-primary)',
        minHeight: 'var(--app-height)',
        paddingTop: 'var(--safe-area-top)',
        paddingBottom: 'calc(8rem + var(--safe-area-bottom))', // 8rem for global player + safe area
      }}
    >
      {/* Sticky Header - Compact on Mobile */}
      <div 
        className="sticky z-40 transition-all duration-300 mobile-header"
        style={{
          top: 0,
          background: 'var(--bg-overlay)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
          paddingTop: 'var(--safe-area-top)',
        }}
      >
        <div className="max-w-4xl mx-auto px-3 py-2 md:px-4 md:py-4 flex items-center justify-between">
          <Link 
            to="/" 
            className="inline-flex items-center gap-1 px-2 py-1 md:px-3 md:py-2 rounded-lg transition-colors active:opacity-70 mobile-back"
            style={{
              color: 'var(--text-urdu)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(148, 163, 184, 0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <span className="text-lg md:text-xl">←</span>
            <span className="text-xs md:text-sm font-medium">Back</span>
          </Link>
          
          <div className="flex-1 text-center px-2 md:px-4 mobile-header-center">
            <h1 className="text-arabic text-base md:text-lg font-bold truncate mobile-header-title" style={{ color: 'var(--text-arabic)' }}>
              {surah.name}
            </h1>
            <p className="text-xs truncate hidden md:block mobile-header-subtitle" style={{ color: 'var(--text-secondary)', opacity: 0.6 }}>
              {surah.englishName}
            </p>
          </div>
          
          <div className="hidden md:flex items-center gap-2">
            {/* Search Button - Desktop Only */}
            <button
              onClick={() => setShowSearchModal(true)}
              className="p-2 rounded-lg transition-all active:scale-95"
              style={{
                background: 'rgba(148, 163, 184, 0.08)',
                border: '1px solid var(--border-default)',
                color: 'var(--text-secondary)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--gold-muted)';
                e.currentTarget.style.borderColor = 'var(--border-gold)';
                e.currentTarget.style.color = 'var(--gold-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(148, 163, 184, 0.08)';
                e.currentTarget.style.borderColor = 'var(--border-default)';
                e.currentTarget.style.color = 'var(--text-secondary)';
              }}
              title="Search"
            >
              🔍
            </button>

            {/* Index Button - Desktop Only */}
            <button
              onClick={() => setShowIndexModal(true)}
              className="p-2 rounded-lg transition-all active:scale-95"
              style={{
                background: 'rgba(148, 163, 184, 0.08)',
                border: '1px solid var(--border-default)',
                color: 'var(--text-secondary)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--gold-muted)';
                e.currentTarget.style.borderColor = 'var(--border-gold)';
                e.currentTarget.style.color = 'var(--gold-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(148, 163, 184, 0.08)';
                e.currentTarget.style.borderColor = 'var(--border-default)';
                e.currentTarget.style.color = 'var(--text-secondary)';
              }}
              title="Index"
            >
              📖
            </button>

            {/* Settings Button - Desktop Only */}
            <button
              onClick={() => setShowSettingsDrawer(true)}
              className="p-2 rounded-lg transition-all active:scale-95"
              style={{
                background: 'rgba(148, 163, 184, 0.08)',
                border: '1px solid var(--border-default)',
                color: 'var(--text-secondary)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--gold-muted)';
                e.currentTarget.style.borderColor = 'var(--border-gold)';
                e.currentTarget.style.color = 'var(--gold-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(148, 163, 184, 0.08)';
                e.currentTarget.style.borderColor = 'var(--border-default)';
                e.currentTarget.style.color = 'var(--text-secondary)';
              }}
              title="Reading Settings"
            >
              ⚙️
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 md:px-4 py-4 md:py-8">
        {/* Surah Header */}
        <div 
          className="text-center mb-12 p-12 rounded-2xl surah-header-card"
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          {/* Names */}
          <h1 className="text-arabic text-5xl md:text-6xl font-bold mb-3 surah-header-title-ar" style={{ color: 'var(--text-arabic)', fontFamily: arabicFontFamily }}>
              {surah.name}
            </h1>
          <h2 className="text-3xl font-semibold mb-2 surah-header-title-en" style={{ color: 'var(--gold-hover)' }}>
              {surah.englishName}
            </h2>
          <p className="text-lg mb-6 surah-header-subtitle" style={{ color: 'var(--text-secondary)', opacity: 0.8 }}>
              {surah.englishNameTranslation}
            </p>

          {/* Metadata */}
          <div className="flex gap-3 justify-center mb-8 flex-wrap surah-header-meta">
            <span 
              className="px-4 py-2 rounded-full text-sm font-medium surah-header-pill"
              style={{
                background: 'var(--button-default)',
                color: 'var(--text-english)',
                border: '2px solid var(--border-strong)',
              }}
            >
              {surah.revelationType === 'Meccan' ? '🕋 Meccan' : '🕌 Medinan'}
            </span>
            <span 
              className="px-4 py-2 rounded-full text-sm font-medium surah-header-pill"
              style={{
                background: 'var(--button-default)',
                color: 'var(--text-english)',
                border: '2px solid var(--border-strong)',
              }}
            >
              📚 {surah.numberOfAyahs} Verses
            </span>
            <span 
              className="px-4 py-2 rounded-full text-sm font-medium surah-header-pill"
              style={{
                background: 'var(--button-default)',
                color: 'var(--text-english)',
                border: '2px solid var(--border-strong)',
              }}
            >
              Surah {surah.number}
            </span>
          </div>

          {/* Bismillah */}
          {surah.showBismillahHeader && (
            <>
              <div className="flex items-center gap-4 my-8 surah-header-divider">
                <div 
                  className="flex-1 h-px"
                  style={{
                    background: 'linear-gradient(to right, transparent, var(--border-gold), transparent)',
                  }}
                />
              </div>
              
              <div
                className="text-arabic text-4xl font-semibold mb-8 surah-header-bismillah"
                style={{
                  color: 'var(--text-arabic)',
                  lineHeight: 2.0,
                  fontFamily: arabicFontFamily,
                }}
              >
                بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
              </div>
              
              <div className="flex items-center gap-4 mb-8 surah-header-divider">
                <div 
                  className="flex-1 h-px"
                  style={{
                    background: 'linear-gradient(to right, transparent, var(--border-gold), transparent)',
                  }}
                />
              </div>
            </>
          )}

          {/* Play Full Surah Button */}
          <div className="mt-4 md:mt-8 surah-header-cta">
            {isFullSurahPlaying() && (
              <div 
                className="mb-4 p-4 rounded-lg border-l-4"
                style={{
                  background: 'var(--gold-muted)',
                  borderColor: 'var(--gold-primary)',
                }}
              >
                <p className="font-semibold text-sm" style={{ color: 'var(--gold-hover)' }}>
                  Full Surah Recitation Playing
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                  Individual verse play buttons are disabled
                </p>
              </div>
            )}
            
            <button
              onClick={handlePlayFullSurah}
              disabled={currentSurah === parseInt(number) && mode === 'single-ayah' && status === 'playing'}
              className="w-full px-8 py-4 rounded-xl font-bold text-lg shadow-lg transition-all active:scale-95 surah-header-cta-btn"
              style={{
                background: isFullSurahPlaying()
                  ? 'linear-gradient(to right, #EF4444, #DC2626)'
                  : (currentSurah === parseInt(number) && mode === 'single-ayah')
                  ? 'rgba(148, 163, 184, 0.3)'
                  : 'linear-gradient(to right, var(--gold-primary), var(--gold-hover))',
                color: isFullSurahPlaying() || !(currentSurah === parseInt(number) && mode === 'single-ayah')
                  ? 'white'
                  : 'var(--text-tertiary)',
                cursor: (currentSurah === parseInt(number) && mode === 'single-ayah' && status === 'playing')
                  ? 'not-allowed'
                  : 'pointer',
                opacity: (currentSurah === parseInt(number) && mode === 'single-ayah' && status === 'playing')
                  ? 0.5
                  : 1,
                minHeight: '56px', // Enhanced touch target
              }}
            >
              {(currentSurah === parseInt(number) && mode === 'single-ayah' && status === 'playing')
                ? 'Stop Individual Verse First'
                : isFullSurahPlaying()
                ? 'Stop Full Surah'
                : 'Play Full Surah Recitation'}
            </button>

            {/* Save place & Resume */}
            <div className="flex gap-3 mt-4 flex-wrap justify-center surah-header-secondary">
              <button
                onClick={handleSavePlace}
                className="px-6 py-3 rounded-xl font-semibold text-sm transition-all active:scale-95 surah-header-save"
                style={{
                  background: savedFeedback ? 'var(--success)' : 'var(--button-default)',
                  border: '2px solid ' + (savedFeedback ? 'var(--success)' : 'var(--border-strong)'),
                  color: savedFeedback ? 'white' : 'var(--text-english)',
                }}
              >
                {savedFeedback ? 'Saved!' : 'Save place'}
              </button>
              {lastRead && (
                <button
                  onClick={handleResumeToLastRead}
                  className="px-6 py-3 rounded-xl font-semibold text-sm transition-all active:scale-95 surah-header-resume"
                  style={{
                    background: 'var(--gold-muted)',
                    border: '2px solid var(--border-gold)',
                    color: 'var(--gold-hover)',
                  }}
                >
                  Resume
                </button>
              )}
              <button
                onClick={handleToggleSurahBookmark}
                className="px-6 py-3 rounded-xl font-semibold text-sm transition-all active:scale-95"
                style={{
                  background: isBookmarked(parseInt(number)) ? 'var(--gold-muted)' : 'var(--button-default)',
                  border: `2px solid ${isBookmarked(parseInt(number)) ? 'var(--border-gold)' : 'var(--border-strong)'}`,
                  color: isBookmarked(parseInt(number)) ? 'var(--gold-hover)' : 'var(--text-english)',
                }}
                aria-label={isBookmarked(parseInt(number)) ? 'Remove surah bookmark' : 'Bookmark this surah'}
              >
                {isBookmarked(parseInt(number)) ? '🔖 Bookmarked' : '🔖 Bookmark'}
              </button>
            </div>
          </div>
        </div>

        {/* Ayahs - Scroll Mode */}
        {readingMode === 'scroll' && (
          <div className="space-y-6">
            {surah.ayahs.map((ayah) => {
              const isPlaying = isCurrentAyahPlaying(ayah.numberInSurah);
              const isDisabled = isFullSurahPlaying();
              const isAyahBookmarked = isBookmarked(parseInt(number), ayah.numberInSurah);
              const isAyahNoted = hasNote(parseInt(number), ayah.numberInSurah);

              return (
                <div
                  key={ayah.number}
                  ref={(el) => ayahRefs.current[ayah.numberInSurah] = el}
                  className={`focus-card ${isPlaying ? 'is-playing' : ''}`}
                  style={{ opacity: isDisabled && !isPlaying ? 0.6 : 1 }}
                >
                  <div className="focus-card-inner">
                    {/* Ayah Number */}
                    <div className="flex-shrink-0 pt-1">
                      <div className={`focus-badge ${isPlaying ? 'is-playing' : ''}`}>
                        {ayah.numberInSurah}
                      </div>
                    </div>

                    {/* Verse Content */}
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-arabic-block focus-arabic ${isPlaying ? 'is-playing' : ''}`}
                        style={{
                          fontSize: `${fontSize}px`,
                          lineHeight: getLineHeightClass(),
                          fontFamily: arabicFontFamily,
                        }}
                      >
                        {ayah.arabic}
                      </div>

                      {showUrdu && (
                        <div
                          className="text-urdu-block focus-translation focus-translation-urdu"
                          style={{ fontSize: `${fontSize - 6}px` }}
                        >
                          {ayah.urdu}
                        </div>
                      )}

                      {showEnglish && (
                        <div
                          className="text-english-block focus-translation focus-translation-en"
                          style={{ fontSize: `${fontSize - 10}px` }}
                        >
                          {ayah.english}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="focus-actions">
                        <div className="focus-actions-left">
                          <button
                            type="button"
                            onClick={() => copyAyah(ayah.arabic, ayah.english, ayah.urdu, ayah.number)}
                            className={`icon-button ${copiedAyah === ayah.number ? 'is-success' : ''}`}
                            aria-label={copiedAyah === ayah.number ? 'Copied verse' : 'Copy verse'}
                            title={copiedAyah === ayah.number ? 'Copied' : 'Copy verse'}
                          >
                            {copiedAyah === ayah.number ? (
                              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            ) : (
                              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 9h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2Zm-4 6V7a2 2 0 0 1 2-2h8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            )}
                          </button>

                          <button
                            type="button"
                            onClick={() => handlePlayAyah(ayah.numberInSurah, ayah.number)}
                            disabled={isDisabled}
                            className={`icon-button ${isPlaying ? 'is-playing' : ''}`}
                            aria-label={isDisabled ? 'Full surah playing' : isPlaying ? 'Pause verse' : 'Play verse'}
                            title={isDisabled ? 'Full surah playing' : isPlaying ? 'Pause verse' : 'Play verse'}
                          >
                            {isPlaying ? (
                              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="currentColor" /></svg>
                            ) : (
                              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5l11 7-11 7V5z" fill="currentColor" /></svg>
                            )}
                          </button>

                          <button
                            type="button"
                            onClick={() => handleToggleAyahBookmark(ayah)}
                            className="icon-button"
                            style={{ color: isAyahBookmarked ? 'var(--gold-hover)' : undefined }}
                            aria-label={isAyahBookmarked ? 'Remove bookmark' : 'Bookmark verse'}
                            title={isAyahBookmarked ? 'Remove bookmark' : 'Bookmark verse'}
                          >
                            {isAyahBookmarked ? (
                              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3h14a2 2 0 0 1 2 2v16l-9-4-9 4V5a2 2 0 0 1 2-2z" fill="currentColor" /></svg>
                            ) : (
                              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3h14a2 2 0 0 1 2 2v16l-9-4-9 4V5a2 2 0 0 1 2-2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            )}
                          </button>

                          <button
                            type="button"
                            onClick={() => handleOpenNote(ayah)}
                            className={`icon-button ${isAyahNoted ? 'is-success' : ''}`}
                            style={{ color: isAyahNoted ? 'rgb(129, 140, 248)' : undefined }}
                            aria-label={isAyahNoted ? 'Edit note' : 'Add note'}
                            title={isAyahNoted ? 'Edit note' : 'Add note'}
                          >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Ayahs - Ayah Focus Mode */}
        {readingMode === 'ayah' && surah.ayahs[currentAyahIndex] && (
          <div className="max-w-4xl mx-auto">
            {/* Ayah Counter (Clean & Centered) */}
            <div className="text-center mb-6 focus-counter">
              <p className="text-base md:text-lg font-bold focus-counter-title" style={{ color: 'var(--text-english)' }}>
                Ayah {currentAyahIndex + 1} of {surah.ayahs.length}
              </p>
              <p className="text-xs md:text-sm mt-1 focus-counter-subtitle" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
                Swipe or use buttons below to navigate
              </p>
            </div>

            {/* Single Ayah Display */}
            <div
              ref={ayahContainerRef}
              className="mt-4"
              style={{
                touchAction: 'pan-y',
              }}
            >
              {(() => {
                const ayah = surah.ayahs[currentAyahIndex];
                const isPlaying = isCurrentAyahPlaying(ayah.numberInSurah);
                const isDisabled = isFullSurahPlaying();
                const isAyahBookmarked = isBookmarked(parseInt(number), ayah.numberInSurah);
                const isAyahNoted = hasNote(parseInt(number), ayah.numberInSurah);

                return (
                  <div
                    className={`focus-card ${isPlaying ? 'is-playing' : ''}`}
                  >
                    <div className="focus-card-inner">
                      {/* Ayah Number */}
                      <div className="flex-shrink-0 pt-1">
                        <div 
                          className={`focus-badge ${isPlaying ? 'is-playing' : ''}`}
                        >
                          {ayah.numberInSurah}
                        </div>
                      </div>

                      {/* Verse Content */}
                      <div className="flex-1 min-w-0">
                        {/* Arabic Text */}
                        <div
                          className={`text-arabic-block focus-arabic ${isPlaying ? 'is-playing' : ''}`}
                          style={{
                            fontSize: `${fontSize}px`,
                            lineHeight: getLineHeightClass(),
                            fontFamily: arabicFontFamily,
                          }}
                        >
                          {ayah.arabic}
                        </div>

                        {/* Urdu Translation */}
                        {showUrdu && (
                          <div 
                            className="text-urdu-block focus-translation focus-translation-urdu" 
                            style={{ 
                              fontSize: `${fontSize - 6}px`,
                              lineHeight: '2.5', // Extra generous line height for Ayah Focus mode
                            }}
                          >
                            {ayah.urdu}
                          </div>
                        )}

                        {/* English Translation */}
                        {showEnglish && (
                          <div 
                            className="text-english-block focus-translation focus-translation-en" 
                            style={{ 
                              fontSize: `${fontSize - 10}px`,
                            }}
                          >
                            {ayah.english}
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="focus-actions">
                          <div className="focus-actions-left">
                            <button
                              type="button"
                              onClick={() => copyAyah(ayah.arabic, ayah.english, ayah.urdu, ayah.number)}
                              className={`icon-button ${copiedAyah === ayah.number ? 'is-success' : ''}`}
                              aria-label={copiedAyah === ayah.number ? 'Copied verse' : 'Copy verse'}
                              title={copiedAyah === ayah.number ? 'Copied' : 'Copy verse'}
                            >
                              {copiedAyah === ayah.number ? (
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              ) : (
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M9 9h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2Zm-4 6V7a2 2 0 0 1 2-2h8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </button>
                            
                            <button
                              type="button"
                              onClick={() => handlePlayAyah(ayah.numberInSurah, ayah.number)}
                              disabled={isDisabled}
                              className={`icon-button ${isPlaying ? 'is-playing' : ''}`}
                              aria-label={
                                isDisabled
                                  ? 'Full surah playing'
                                  : isPlaying
                                  ? 'Pause verse'
                                  : 'Play verse'
                              }
                              title={
                                isDisabled
                                  ? 'Full surah playing'
                                  : isPlaying
                                  ? 'Pause verse'
                                  : 'Play verse'
                              }
                            >
                              {isPlaying ? (
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="currentColor" />
                                </svg>
                              ) : (
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M8 5l11 7-11 7V5z" fill="currentColor" />
                                </svg>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggleAyahBookmark(ayah)}
                              className="icon-button"
                              style={{ color: isAyahBookmarked ? 'var(--gold-hover)' : undefined }}
                              aria-label={isAyahBookmarked ? 'Remove bookmark' : 'Bookmark verse'}
                              title={isAyahBookmarked ? 'Remove bookmark' : 'Bookmark verse'}
                            >
                              {isAyahBookmarked ? (
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M5 3h14a2 2 0 0 1 2 2v16l-9-4-9 4V5a2 2 0 0 1 2-2z" fill="currentColor" />
                                </svg>
                              ) : (
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M5 3h14a2 2 0 0 1 2 2v16l-9-4-9 4V5a2 2 0 0 1 2-2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenNote(ayah)}
                              className={`icon-button ${isAyahNoted ? 'is-success' : ''}`}
                              style={{ color: isAyahNoted ? 'rgb(129, 140, 248)' : undefined }}
                              aria-label={isAyahNoted ? 'Edit note' : 'Add note'}
                              title={isAyahNoted ? 'Edit note' : 'Add note'}
                            >
                              <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
        </div>

            {/* Bottom Navigation - Primary Controls */}
            <div className="focus-nav">
              <button
                onClick={handlePrevAyah}
                disabled={currentAyahIndex === 0}
                className="focus-nav-btn"
              >
                <span className="focus-nav-icon" aria-hidden="true">←</span>
                <span className="focus-nav-text">Previous Ayah</span>
              </button>

              <button
                onClick={handleNextAyah}
                disabled={currentAyahIndex === surah.ayahs.length - 1}
                className="focus-nav-btn"
              >
                <span className="focus-nav-text">Next Ayah</span>
                <span className="focus-nav-icon" aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Search Modal */}
      <SearchModal 
        isOpen={showSearchModal} 
        onClose={() => setShowSearchModal(false)}
        surahs={allSurahs}
      />

      {/* Index Modal */}
      <IndexModal 
        isOpen={showIndexModal} 
        onClose={() => setShowIndexModal(false)}
        surahs={allSurahs}
      />

      {/* Settings Drawer */}
      <SettingsDrawer
        isOpen={showSettingsDrawer}
        onClose={() => setShowSettingsDrawer(false)}
        fontSize={fontSize}
        setFontSize={setFontSize}
        fontType={fontType}
        setFontType={setFontType}
        lineSpacing={lineSpacing}
        setLineSpacing={setLineSpacing}
        showUrdu={showUrdu}
        setShowUrdu={setShowUrdu}
        showEnglish={showEnglish}
        setShowEnglish={setShowEnglish}
        readingMode={readingMode}
        setReadingMode={setReadingMode}
      />

      {/* Note Modal */}
      <NoteModal
        isOpen={isNoteModalOpen}
        onClose={() => setIsNoteModalOpen(false)}
        surahNumber={parseInt(number)}
        ayahNumber={activeNoteAyah?.numberInSurah}
        surahName={surah?.name}
        ayahText={activeNoteAyah?.english}
      />

      {/* Bookmarks / Notes Panel */}
      <BookmarksNotesPanel
        isOpen={showBookmarksPanel}
        onClose={() => setShowBookmarksPanel(false)}
      />

      {/* Bottom Navigation - Mobile Only */}
      <BottomNavigation
        onSearchClick={() => setShowSearchModal(true)}
        onIndexClick={() => setShowIndexModal(true)}
        onSettingsClick={() => setShowSettingsDrawer(true)}
        hasBookmarks={hasBookmarks}
        onBookmarksClick={() => setShowBookmarksPanel(true)}
      />
    </div>
  );
};

export default SurahDetail;
